// src/services/resourcesApi.ts
import { apiClient } from "./apiClient";
import {
  ResourceSummary,
  ResourceDetails,
  ResourceMember,
  UpdateResourceRequest,
} from "@/types";

export interface GroupCreateDefaultUser {
  id?: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  department?: string;
  employeeDeptId?: string;
}

export interface GroupCreateDefaults {
  primaryOwner?: GroupCreateDefaultUser | null;
  secondaryOwner?: GroupCreateDefaultUser | null;
  businessUnit?: string | null;
  costCenter?: string | null;
}

export interface CreateGroupRequest {
  title: string;
  description?: string;
  primaryOwnerEmail: string;
  secondaryOwnerEmail?: string;
  businessUnit?: string;
  costCenter?: string;
}

export interface CreateGroupResult {
  groupId: string;
  displayName: string;
  description?: string | null;
  mail?: string | null;
  mailNickname?: string | null;
}

// ----- Named functions (recommended) -----

export async function getMyResources(): Promise<ResourceSummary[]> {
  const res = await apiClient.get<ResourceSummary[]>("/api/resources/my");
  return res.data;
}

export async function getGroupCreateDefaults(): Promise<GroupCreateDefaults> {
  const res = await apiClient.get<GroupCreateDefaults>("/api/users/me/group-create-defaults");
  return res.data;
}

export async function createGroup(request: CreateGroupRequest): Promise<CreateGroupResult> {
  const res = await apiClient.post<CreateGroupResult>("/api/resources/groups", request);
  return res.data;
}

export async function getAllResources(): Promise<ResourceSummary[]> {
  const res = await apiClient.get<ResourceSummary[]>("/api/resources");
  return res.data;
}

export async function getResourceDetails(
  internalResourceId: string
): Promise<ResourceDetails> {
  const res = await apiClient.get<ResourceDetails>(
    `/api/resources/${internalResourceId}`
  );
  return res.data;
}

export async function getResourceMembers(
  internalResourceId: string
): Promise<ResourceMember[]> {
  const res = await apiClient.get<ResourceMember[]>(
    `/api/resources/${internalResourceId}/members`
  );
  return res.data;
}

export async function syncResourceMembers(
  internalResourceId: string
): Promise<{ syncedCount: number; members: ResourceMember[] }> {
  const res = await apiClient.post<{ syncedCount: number; members: ResourceMember[] }>(
    `/api/resources/${internalResourceId}/members/sync`
  );
  return res.data;
}

export async function updateResource(
  internalResourceId: string,
  payload: UpdateResourceRequest
): Promise<void> {
  await apiClient.put(`/api/resources/${internalResourceId}`, payload);
}

export async function addResourceMember(
  internalResourceId: string,
  userId: string,
  role: "Owner" | "Member"
): Promise<void> {
  await apiClient.post(`/api/resources/${internalResourceId}/members`, {
    userId,
    role,
  });
}

export async function addGraphMember(
  graphId: string,
  userId: string,
  role: "Owner" | "Member"
): Promise<void> {
  await apiClient.post(`/api/resources/graph/${encodeURIComponent(graphId)}/members`, {
    userId,
    role,
  });
}

export async function removeResourceMember(
  internalResourceId: string,
  userId: string
): Promise<void> {
  await apiClient.delete(`/api/resources/${internalResourceId}/members/${userId}`);
}

export async function removeGraphMember(
  graphId: string,
  userId: string,
  role?: "Owner" | "Member" | "Both"
): Promise<void> {
  await apiClient.delete(`/api/resources/graph/${encodeURIComponent(graphId)}/members/${encodeURIComponent(userId)}`,
    role ? { params: { role } } : undefined);
}

export async function updateMemberRole(
  internalResourceId: string,
  userId: string,
  newRole: "Owner" | "Member"
): Promise<void> {
  await apiClient.patch(`/api/resources/${internalResourceId}/members/${userId}`, {
    role: newRole,
  });
}

export async function updateGraphMemberRole(
  graphId: string,
  userId: string,
  newRole: "Owner" | "Member"
): Promise<void> {
  await apiClient.patch(`/api/resources/graph/${encodeURIComponent(graphId)}/members/${encodeURIComponent(userId)}`, {
    role: newRole,
  });
}

export async function searchUsers(query: string): Promise<any[]> {
  const res = await apiClient.get(`/api/users/search?q=${encodeURIComponent(query)}`);
  return res.data;
}

export async function getCostCenterSuggestions(max = 250): Promise<string[]> {
  const res = await apiClient.get<string[]>(`/api/users/cost-centers?max=${max}`);
  return res.data;
}

export async function getResourceLists(
  internalResourceId: string
): Promise<any[]> {
  const res = await apiClient.get(`/api/resources/${internalResourceId}/lists`);
  return res.data;
}

export async function getResourceListsByGraphId(
  graphId: string
): Promise<any[]> {
  const res = await apiClient.get(`/api/resources/graph/${encodeURIComponent(graphId)}/lists`);
  return res.data;
}

export async function getResourceSharingLinks(
  internalResourceId: string
): Promise<any[]> {
  const res = await apiClient.get(`/api/resources/${internalResourceId}/sharing-links`);
  return res.data;
}

export async function getListItems(
  internalResourceId: string,
  listId: string
): Promise<any[]> {
  console.log('[getListItems] Calling API with:', { internalResourceId, listId });
  const res = await apiClient.get(`/api/resources/${internalResourceId}/lists/${listId}/items`);
  console.log('[getListItems] API response:', res.data?.length, res.data);
  return res.data;
}

export async function getListItemsByGraphId(
  graphId: string,
  listId: string
): Promise<any[]> {
  const res = await apiClient.get(`/api/resources/graph/${encodeURIComponent(graphId)}/lists/${encodeURIComponent(listId)}/items`);
  return res.data;
}

export async function getSiteSavedData(
  internalResourceId: string
): Promise<any> {
  const res = await apiClient.get(`/api/resources/${internalResourceId}/saved-data`);
  return res.data;
}

export async function getSiteSavedDataByGraphId(
  graphId: string
): Promise<any> {
  const res = await apiClient.get(`/api/resources/graph/${encodeURIComponent(graphId)}/saved-data`);
  return res.data;
}

export async function updateSiteSavedData(
  internalResourceId: string,
  data: any
): Promise<void> {
  await apiClient.put(`/api/resources/${internalResourceId}/saved-data`, data);
}

export async function updateSiteSavedDataByGraphId(
  graphId: string,
  data: any
): Promise<void> {
  await apiClient.put(`/api/resources/graph/${encodeURIComponent(graphId)}/saved-data`, data);
}

export async function confirmMetadataReview(graphId: string): Promise<void> {
  await apiClient.put(`/api/resources/graph/${encodeURIComponent(graphId)}/metadata-review-confirm`);
}

// ----- New endpoints for live Graph API data (MVP) -----

export async function getGroupByGraphId(graphId: string): Promise<ResourceDetails> {
  const res = await apiClient.get<ResourceDetails>(
    `/api/resources/graph/${graphId}`
  );
  return res.data;
}

export async function getGroupMembersByGraphId(graphId: string): Promise<ResourceMember[]> {
  const res = await apiClient.get<ResourceMember[]>(
    `/api/resources/graph/${graphId}/members`
  );
  return res.data;
}

export interface GroupReportRun {
  m365ReportRunId: number;
  reportTitle: string;
  reportStatus: string;
  createDt: string;
  generatedReportsCount: number;
}

export interface GroupGeneratedReport {
  m365AdminReportId: number;
  m365ReportRunId: number;
  reportTitle: string;
  reportFilename: string;
  reportRecipient?: string;
  createDt: string;
}

export interface GroupReportsResponse {
  runs: GroupReportRun[];
  generated: GroupGeneratedReport[];
}

export async function getGroupReports(groupId: string): Promise<GroupReportsResponse> {
  const res = await apiClient.get<GroupReportsResponse>(`/api/sharepoint/${encodeURIComponent(groupId)}/reports`);
  return res.data;
}

// ----- Optional object export for older callers -----

export const resourcesApi = {
  getMyResources,
  getAllResources,
  getResourceDetails,
  getResourceMembers,
  syncResourceMembers,
  updateResource,
  addResourceMember,
  addGraphMember,
  removeResourceMember,
  removeGraphMember,
  updateMemberRole,
  updateGraphMemberRole,
  searchUsers,
  getCostCenterSuggestions,
  getResourceLists,
  getResourceListsByGraphId,
  getResourceSharingLinks,
  getListItems,
  getListItemsByGraphId,
  getSiteSavedData,
  getSiteSavedDataByGraphId,
  updateSiteSavedData,
  updateSiteSavedDataByGraphId,
  getGroupByGraphId,
  getGroupMembersByGraphId,
  getGroupReports,
};